import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #quantityUpdateTimers = new Map();
  #pendingUpdates = [];
  #updateInProgress = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    this.#quantityUpdateTimers.forEach((timer) => clearTimeout(timer));
    this.#quantityUpdateTimers.clear();
  }

  /**
   * Debounces quantity changes per cart line so actions on different rows are never collapsed.
   * Removal requests are queued immediately.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #handleQuantitySelectorUpdate = (event) => {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const pendingTimer = this.#quantityUpdateTimers.get(line);
    if (pendingTimer) clearTimeout(pendingTimer);

    if (quantity === 0) {
      this.#quantityUpdateTimers.delete(line);
      this.#onQuantityChange(event);
      return;
    }

    const timer = setTimeout(() => {
      this.#quantityUpdateTimers.delete(line);
      this.#onQuantityChange(event);
    }, 300);

    this.#quantityUpdateTimers.set(line, timer);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const rowKey = row.dataset.key;
      const remove = () => {
        if (!row.isConnected || row.dataset.key !== rowKey || !row.classList.contains('removing')) return;
        row.remove();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const performanceMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const lineItemRow = this.refs.cartItemRows[config.line - 1];
    const { cartTotal } = this.refs;

    this.#disableCartItem(lineItemRow);
    cartTotal?.shimmer();

    this.#pendingUpdates.push({
      ...config,
      key: lineItemRow?.dataset.key || '',
      performanceMarker,
    });

    this.#processUpdateQueue();
  }

  async #processUpdateQueue() {
    if (this.#updateInProgress) return;

    this.#updateInProgress = true;
    let latestResponse = null;

    try {
      while (this.#pendingUpdates.length > 0) {
        const update = this.#pendingUpdates.shift();
        if (!update) continue;

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionsToUpdate = new Set([this.sectionId]);
        cartItemsComponents.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionsToUpdate.add(item.dataset.sectionId);
          }
        });

        const changeTarget = update.key ? { id: update.key } : { line: update.line };
        const body = JSON.stringify({
          ...changeTarget,
          quantity: update.quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
          const parsedResponse = await response.json();

          if (parsedResponse.errors) {
            resetShimmer(this);
            this.#handleCartError(update.line, parsedResponse);
            this.#pendingUpdates.length = 0;
            return;
          }

          latestResponse = parsedResponse;
        } finally {
          cartPerformance.measureFromMarker(update.performanceMarker);
        }
      }

      if (!latestResponse) return;

      resetShimmer(this);

      const sectionHtml = latestResponse.sections?.[this.sectionId];
      if (!sectionHtml) {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent(latestResponse, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: latestResponse.sections,
        })
      );

      morphSection(this.sectionId, sectionHtml);
    } catch (error) {
      this.#pendingUpdates.length = 0;
      console.error(error);
    } finally {
      this.#updateInProgress = false;

      if (this.isConnected) {
        this.#enableCartItems();
      }

      if (this.#pendingUpdates.length > 0) {
        this.#processUpdateQueue();
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables only the cart row being updated so other lines can still queue actions.
   * @param {HTMLTableRowElement | undefined} row - The cart item row.
   */
  #disableCartItem(row) {
    if (!row) return;

    row.dataset.cartMutationPending = 'true';
    row.setAttribute('aria-busy', 'true');

    row.querySelectorAll('button, input, select').forEach((control) => {
      if (
        !(control instanceof HTMLButtonElement) &&
        !(control instanceof HTMLInputElement) &&
        !(control instanceof HTMLSelectElement)
      ) {
        return;
      }

      if (control.disabled) return;

      control.disabled = true;
      control.dataset.cartMutationDisabled = 'true';
    });
  }

  /**
   * Re-enables controls disabled by cart mutations.
   */
  #enableCartItems() {
    this.querySelectorAll('[data-cart-mutation-disabled="true"]').forEach((control) => {
      if (
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement ||
        control instanceof HTMLSelectElement
      ) {
        control.disabled = false;
      }

      delete control.dataset.cartMutationDisabled;
    });

    this.querySelectorAll('[data-cart-mutation-pending="true"]').forEach((row) => {
      delete row.dataset.cartMutationPending;
      row.removeAttribute('aria-busy');
    });
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
